import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  UserIcon,
  MapPinIcon,
  CheckCircleIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  CubeIcon,
  TruckIcon
} from '@heroicons/react/24/outline'
import { useNavigate } from 'react-router-dom'

// Interfaces
interface DatosRemitente {
  nombre: string
  empresa: string
  telefono: string
  email: string
  direccion: string
  ciudad: string
  codigoPostal: string
  identificacion: string
}

interface DatosDestinatario {
  nombre: string
  empresa: string
  telefono: string
  email: string
  direccion: string
  ciudad: string
  codigoPostal: string
  identificacion: string
  instruccionesEntrega: string
}

interface DatosPaquete {
  descripcion: string
  peso: number
  largo: number
  ancho: number
  alto: number
  valor: number
  categoria: string
  fragil: boolean
  requiereRefrigeracion: boolean
  contenido: string
}

interface DatosEnvio {
  tipoServicio: string
  prioridad: string
  fechaEntregaDeseada: string
  horaEntregaDeseada: string
  metodoPago: string
  observaciones: string
  seguro: boolean
  confirmacionEntrega: boolean
  notificacionSMS: boolean
  notificacionEmail: boolean
}

const PaginaCrearEnvio: React.FC = () => {
  const navigate = useNavigate()
  const [pasoActual, setPasoActual] = useState(1)
  const [cargando, setCargando] = useState(false)

  // Estados del formulario
  const [remitente, setRemitente] = useState<DatosRemitente>({
    nombre: '',
    empresa: '',
    telefono: '',
    email: '',
    direccion: '',
    ciudad: '',
    codigoPostal: '',
    identificacion: ''
  })

  const [destinatario, setDestinatario] = useState<DatosDestinatario>({
    nombre: '',
    empresa: '',
    telefono: '',
    email: '',
    direccion: '',
    ciudad: '',
    codigoPostal: '',
    identificacion: '',
    instruccionesEntrega: ''
  })

  const [paquete, setPaquete] = useState<DatosPaquete>({
    descripcion: '',
    peso: 0,
    largo: 0,
    ancho: 0,
    alto: 0,
    valor: 0,
    categoria: '',
    fragil: false,
    requiereRefrigeracion: false,
    contenido: ''
  })

  const [envio, setEnvio] = useState<DatosEnvio>({
    tipoServicio: '',
    prioridad: '',
    fechaEntregaDeseada: '',
    horaEntregaDeseada: '',
    metodoPago: '',
    observaciones: '',
    seguro: false,
    confirmacionEntrega: true,
    notificacionSMS: true,
    notificacionEmail: true
  })

  const [errores, setErrores] = useState<Record<string, string>>({})

  // Configuración de pasos
  const pasos = [
    {
      numero: 1,
      titulo: 'Datos del Remitente',
      icono: UserIcon,
      descripcion: 'Información de quien envía'
    },
    {
      numero: 2,
      titulo: 'Datos del Destinatario',
      icono: MapPinIcon,
      descripcion: 'Información de quien recibe'
    },
    {
      numero: 3,
      titulo: 'Información del Paquete',
      icono: CubeIcon,
      descripcion: 'Detalles del envío'
    },
    {
      numero: 4,
      titulo: 'Configuración del Envío',
      icono: TruckIcon,
      descripcion: 'Opciones de entrega'
    },
    {
      numero: 5,
      titulo: 'Confirmación',
      icono: CheckCircleIcon,
      descripcion: 'Revisar y confirmar'
    }
  ]

  // Validaciones
  const validarPaso = (paso: number): boolean => {
    const nuevosErrores: Record<string, string> = {}

    switch (paso) {
      case 1:
        if (!remitente.nombre) nuevosErrores.remitenteNombre = 'El nombre es requerido'
        if (!remitente.telefono) nuevosErrores.remitenteTelefono = 'El teléfono es requerido'
        if (!remitente.direccion) nuevosErrores.remitenteDireccion = 'La dirección es requerida'
        if (!remitente.ciudad) nuevosErrores.remitenteCiudad = 'La ciudad es requerida'
        break
      case 2:
        if (!destinatario.nombre) nuevosErrores.destinatarioNombre = 'El nombre es requerido'
        if (!destinatario.telefono) nuevosErrores.destinatarioTelefono = 'El teléfono es requerido'
        if (!destinatario.direccion) nuevosErrores.destinatarioDireccion = 'La dirección es requerida'
        if (!destinatario.ciudad) nuevosErrores.destinatarioCiudad = 'La ciudad es requerida'
        break
      case 3:
        if (!paquete.descripcion) nuevosErrores.paqueteDescripcion = 'La descripción es requerida'
        if (paquete.peso <= 0) nuevosErrores.paquetePeso = 'El peso debe ser mayor a 0'
        if (paquete.largo <= 0) nuevosErrores.paqueteLargo = 'El largo debe ser mayor a 0'
        if (paquete.ancho <= 0) nuevosErrores.paqueteAncho = 'El ancho debe ser mayor a 0'
        if (paquete.alto <= 0) nuevosErrores.paqueteAlto = 'El alto debe ser mayor a 0'
        if (!paquete.categoria) nuevosErrores.paqueteCategoria = 'La categoría es requerida'
        break
      case 4:
        if (!envio.tipoServicio) nuevosErrores.envioTipoServicio = 'El tipo de servicio es requerido'
        if (!envio.prioridad) nuevosErrores.envioPrioridad = 'La prioridad es requerida'
        if (!envio.metodoPago) nuevosErrores.envioMetodoPago = 'El método de pago es requerido'
        break
    }

    setErrores(nuevosErrores)
    return Object.keys(nuevosErrores).length === 0
  }

  // Navegación entre pasos
  const siguientePaso = () => {
    if (validarPaso(pasoActual)) {
      setPasoActual(pasoActual + 1)
    }
  }

  const pasoAnterior = () => {
    setPasoActual(pasoActual - 1)
  }

  // Calcular costo estimado
  const calcularCostoEstimado = (): number => {
    const pesoBase = paquete.peso * 2000 // $2000 por kg
    const volumen = (paquete.largo * paquete.ancho * paquete.alto) / 1000000 // m³
    const costoVolumen = volumen * 15000 // $15000 por m³
    const costoBase = Math.max(pesoBase, costoVolumen)
    
    let multiplicador = 1
    if (envio.prioridad === 'express') multiplicador = 1.5
    if (envio.prioridad === 'urgente') multiplicador = 2
    
    const costoFinal = costoBase * multiplicador
    return Math.max(costoFinal, 15000) // Mínimo $15,000
  }

  // Enviar formulario
  const enviarFormulario = async () => {
    setCargando(true)
    
    // Simular envío
    await new Promise(resolve => setTimeout(resolve, 2000))
    
    // Aquí iría la lógica real de envío
    console.log('Datos del envío:', { remitente, destinatario, paquete, envio })
    
    setCargando(false)
    navigate('/dashboard/envios')
  }

  // Formatear moneda
  const formatearMoneda = (valor: number) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(valor)
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <button
            onClick={() => navigate('/dashboard/envios')}
            className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 mb-4"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Volver a Envíos
          </button>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 flex items-center">
            <CubeIcon className="h-8 w-8 text-blue-500 mr-3" />
            Crear Nuevo Envío
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Complete la información para crear un nuevo envío
          </p>
        </motion.div>

        {/* Indicador de progreso */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 mb-6"
        >
          <div className="flex items-center justify-between">
            {pasos.map((paso, index) => {
              const Icono = paso.icono
              const esActual = paso.numero === pasoActual
              const esCompletado = paso.numero < pasoActual
              
              return (
                <div key={paso.numero} className="flex items-center">
                  <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                    esCompletado 
                      ? 'bg-green-500 border-green-500 text-white'
                      : esActual
                      ? 'bg-blue-500 border-blue-500 text-white'
                      : 'bg-gray-100 border-gray-300 text-gray-400 dark:bg-gray-700 dark:border-gray-600'
                  }`}>
                    {esCompletado ? (
                      <CheckCircleIcon className="h-6 w-6" />
                    ) : (
                      <Icono className="h-5 w-5" />
                    )}
                  </div>
                  <div className="ml-3 hidden sm:block">
                    <p className={`text-sm font-medium ${
                      esActual ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'
                    }`}>
                      {paso.titulo}
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500">
                      {paso.descripcion}
                    </p>
                  </div>
                  {index < pasos.length - 1 && (
                    <div className={`w-12 h-0.5 mx-4 ${
                      paso.numero < pasoActual ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
                    }`} />
                  )}
                </div>
              )
            })}
          </div>
        </motion.div>

        {/* Contenido del formulario */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6"
        >
          <AnimatePresence mode="wait">
            {/* Paso 1: Datos del Remitente */}
            {pasoActual === 1 && (
              <motion.div
                key="paso1"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                className="space-y-6"
              >
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                  <UserIcon className="h-6 w-6 text-blue-500 mr-2" />
                  Datos del Remitente
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="envio-crear-remitente-nombre" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Nombre completo *
                    </label>
                    <input
                      id="envio-crear-remitente-nombre"
                      name="remitente.nombre"
                      type="text"
                      value={remitente.nombre}
                      onChange={(e) => setRemitente({ ...remitente, nombre: e.target.value })}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${
                        errores.remitenteNombre ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                      }`}
                      placeholder="Ingrese el nombre completo"
                      aria-invalid={!!errores.remitenteNombre}
                      aria-describedby={errores.remitenteNombre ? 'error-envio-crear-remitente-nombre' : undefined}
                    />
                    {errores.remitenteNombre && (
                      <p id="error-envio-crear-remitente-nombre" role="alert" className="mt-1 text-sm text-red-600">{errores.remitenteNombre}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="envio-crear-remitente-empresa" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Empresa (opcional)
                    </label>
                    <input
                      id="envio-crear-remitente-empresa"
                      name="remitente.empresa"
                      type="text"
                      value={remitente.empresa}
                      onChange={(e) => setRemitente({ ...remitente, empresa: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      placeholder="Nombre de la empresa"
                    />
                  </div>

                  <div>
                    <label htmlFor="envio-crear-remitente-telefono" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Teléfono *
                    </label>
                    <input
                      id="envio-crear-remitente-telefono"
                      name="remitente.telefono"
                      type="tel"
                      value={remitente.telefono}
                      onChange={(e) => setRemitente({ ...remitente, telefono: e.target.value })}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${
                        errores.remitenteTelefono ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                      }`}
                      placeholder="+57 300 123 4567"
                      aria-invalid={!!errores.remitenteTelefono}
                      aria-describedby={errores.remitenteTelefono ? 'error-envio-crear-remitente-telefono' : undefined}
                    />
                    {errores.remitenteTelefono && (
                      <p id="error-envio-crear-remitente-telefono" role="alert" className="mt-1 text-sm text-red-600">{errores.remitenteTelefono}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="envio-crear-remitente-email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Email
                    </label>
                    <input
                      id="envio-crear-remitente-email"
                      name="remitente.email"
                      type="email"
                      value={remitente.email}
                      onChange={(e) => setRemitente({ ...remitente, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      placeholder="correo@ejemplo.com"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label htmlFor="envio-crear-remitente-direccion" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Dirección completa *
                    </label>
                    <input
                      id="envio-crear-remitente-direccion"
                      name="remitente.direccion"
                      type="text"
                      value={remitente.direccion}
                      onChange={(e) => setRemitente({ ...remitente, direccion: e.target.value })}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${
                        errores.remitenteDireccion ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                      }`}
                      placeholder="Calle 123 #45-67, Barrio, Ciudad"
                      aria-invalid={!!errores.remitenteDireccion}
                      aria-describedby={errores.remitenteDireccion ? 'error-envio-crear-remitente-direccion' : undefined}
                    />
                    {errores.remitenteDireccion && (
                      <p id="error-envio-crear-remitente-direccion" role="alert" className="mt-1 text-sm text-red-600">{errores.remitenteDireccion}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="envio-crear-remitente-ciudad" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Ciudad *
                    </label>
                    <select
                      id="envio-crear-remitente-ciudad"
                      name="remitente.ciudad"
                      value={remitente.ciudad}
                      onChange={(e) => setRemitente({ ...remitente, ciudad: e.target.value })}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${
                        errores.remitenteCiudad ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                      }`}
                      aria-invalid={!!errores.remitenteCiudad}
                      aria-describedby={errores.remitenteCiudad ? 'error-envio-crear-remitente-ciudad' : undefined}
                    >
                      <option value="">Seleccione una ciudad</option>
                      <option value="bogota">Bogotá</option>
                      <option value="medellin">Medellín</option>
                      <option value="cali">Cali</option>
                      <option value="barranquilla">Barranquilla</option>
                      <option value="cartagena">Cartagena</option>
                      <option value="bucaramanga">Bucaramanga</option>
                    </select>
                    {errores.remitenteCiudad && (
                      <p id="error-envio-crear-remitente-ciudad" role="alert" className="mt-1 text-sm text-red-600">{errores.remitenteCiudad}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="envio-crear-remitente-codigo-postal" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Código postal
                    </label>
                    <input
                      id="envio-crear-remitente-codigo-postal"
                      name="remitente.codigoPostal"
                      type="text"
                      value={remitente.codigoPostal}
                      onChange={(e) => setRemitente({ ...remitente, codigoPostal: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      placeholder="110111"
                    />
                  </div>

                  <div>
                    <label htmlFor="envio-crear-remitente-identificacion" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      N° Identificación
                    </label>
                    <input
                      id="envio-crear-remitente-identificacion"
                      name="remitente.identificacion"
                      type="text"
                      value={remitente.identificacion}
                      onChange={(e) => setRemitente({ ...remitente, identificacion: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      placeholder="CC / NIT / Pasaporte"
                    />
                  </div>
                </div>
              </motion.div>
            )}

            {/* Paso 2: Datos del Destinatario */}
            {pasoActual === 2 && (
              <motion.div
                key="paso2"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                className="space-y-6"
              >
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                  <MapPinIcon className="h-6 w-6 text-blue-500 mr-2" />
                  Datos del Destinatario
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="envio-crear-destinatario-nombre" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Nombre completo *
                    </label>
                    <input
                      id="envio-crear-destinatario-nombre"
                      name="destinatario.nombre"
                      type="text"
                      value={destinatario.nombre}
                      onChange={(e) => setDestinatario({ ...destinatario, nombre: e.target.value })}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${
                        errores.destinatarioNombre ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                      }`}
                      placeholder="Ingrese el nombre completo"
                      aria-invalid={!!errores.destinatarioNombre}
                      aria-describedby={errores.destinatarioNombre ? 'error-envio-crear-destinatario-nombre' : undefined}
                    />
                    {errores.destinatarioNombre && (
                      <p id="error-envio-crear-destinatario-nombre" role="alert" className="mt-1 text-sm text-red-600">{errores.destinatarioNombre}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="envio-crear-destinatario-empresa" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Empresa (opcional)
                    </label>
                    <input
                      id="envio-crear-destinatario-empresa"
                      name="destinatario.empresa"
                      type="text"
                      value={destinatario.empresa}
                      onChange={(e) => setDestinatario({ ...destinatario, empresa: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      placeholder="Nombre de la empresa"
                    />
                  </div>

                  <div>
                    <label htmlFor="envio-crear-destinatario-telefono" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Teléfono *
                    </label>
                    <input
                      id="envio-crear-destinatario-telefono"
                      name="destinatario.telefono"
                      type="tel"
                      value={destinatario.telefono}
                      onChange={(e) => setDestinatario({ ...destinatario, telefono: e.target.value })}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${
                        errores.destinatarioTelefono ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                      }`}
                      placeholder="+57 300 123 4567"
                      aria-invalid={!!errores.destinatarioTelefono}
                      aria-describedby={errores.destinatarioTelefono ? 'error-envio-crear-destinatario-telefono' : undefined}
                    />
                    {errores.destinatarioTelefono && (
                      <p id="error-envio-crear-destinatario-telefono" role="alert" className="mt-1 text-sm text-red-600">{errores.destinatarioTelefono}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="envio-crear-destinatario-email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Email
                    </label>
                    <input
                      id="envio-crear-destinatario-email"
                      name="destinatario.email"
                      type="email"
                      value={destinatario.email}
                      onChange={(e) => setDestinatario({ ...destinatario, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      placeholder="correo@ejemplo.com"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label htmlFor="envio-crear-destinatario-direccion" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Dirección completa *
                    </label>
                    <input
                      id="envio-crear-destinatario-direccion"
                      name="destinatario.direccion"
                      type="text"
                      value={destinatario.direccion}
                      onChange={(e) => setDestinatario({ ...destinatario, direccion: e.target.value })}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${
                        errores.destinatarioDireccion ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                      }`}
                      placeholder="Calle 123 #45-67, Barrio, Ciudad"
                      aria-invalid={!!errores.destinatarioDireccion}
                      aria-describedby={errores.destinatarioDireccion ? 'error-envio-crear-destinatario-direccion' : undefined}
                    />
                    {errores.destinatarioDireccion && (
                      <p id="error-envio-crear-destinatario-direccion" role="alert" className="mt-1 text-sm text-red-600">{errores.destinatarioDireccion}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="envio-crear-destinatario-ciudad" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Ciudad *
                    </label>
                    <select
                      id="envio-crear-destinatario-ciudad"
                      name="destinatario.ciudad"
                      value={destinatario.ciudad}
                      onChange={(e) => setDestinatario({ ...destinatario, ciudad: e.target.value })}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${
                        errores.destinatarioCiudad ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                      }`}
                      aria-invalid={!!errores.destinatarioCiudad}
                      aria-describedby={errores.destinatarioCiudad ? 'error-envio-crear-destinatario-ciudad' : undefined}
                    >
                      <option value="">Seleccione una ciudad</option>
                      <option value="bogota">Bogotá</option>
                      <option value="medellin">Medellín</option>
                      <option value="cali">Cali</option>
                      <option value="barranquilla">Barranquilla</option>
                      <option value="cartagena">Cartagena</option>
                      <option value="bucaramanga">Bucaramanga</option>
                    </select>
                    {errores.destinatarioCiudad && (
                      <p id="error-envio-crear-destinatario-ciudad" role="alert" className="mt-1 text-sm text-red-600">{errores.destinatarioCiudad}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="envio-crear-destinatario-codigo-postal" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Código postal
                    </label>
                    <input
                      id="envio-crear-destinatario-codigo-postal"
                      name="destinatario.codigoPostal"
                      type="text"
                      value={destinatario.codigoPostal}
                      onChange={(e) => setDestinatario({ ...destinatario, codigoPostal: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      placeholder="110111"
                    />
                  </div>

                  <div>
                    <label htmlFor="envio-crear-destinatario-identificacion" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      N° Identificación
                    </label>
                    <input
                      id="envio-crear-destinatario-identificacion"
                      name="destinatario.identificacion"
                      type="text"
                      value={destinatario.identificacion}
                      onChange={(e) => setDestinatario({ ...destinatario, identificacion: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      placeholder="CC / NIT / Pasaporte"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label htmlFor="envio-crear-destinatario-instrucciones-entrega" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Instrucciones de entrega
                    </label>
                    <textarea
                      id="envio-crear-destinatario-instrucciones-entrega"
                      name="destinatario.instruccionesEntrega"
                      value={destinatario.instruccionesEntrega}
                      onChange={(e) => setDestinatario({ ...destinatario, instruccionesEntrega: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      placeholder="Instrucciones especiales para la entrega..."
                    />
                  </div>
                </div>
              </motion.div>
            )}

            {/* Paso 3: Información del Paquete */}
            {pasoActual === 3 && (
              <motion.div
                key="paso3"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                className="space-y-6"
              >
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                  <CubeIcon className="h-6 w-6 text-blue-500 mr-2" />
                  Información del Paquete
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label htmlFor="envio-crear-paquete-descripcion" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Descripción del contenido *
                    </label>
                    <input
                      id="envio-crear-paquete-descripcion"
                      name="paquete.descripcion"
                      type="text"
                      value={paquete.descripcion}
                      onChange={(e) => setPaquete({ ...paquete, descripcion: e.target.value })}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${
                        errores.paqueteDescripcion ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                      }`}
                      placeholder="Ej: Documentos, ropa, electrónicos..."
                      aria-invalid={!!errores.paqueteDescripcion}
                      aria-describedby={errores.paqueteDescripcion ? 'error-envio-crear-paquete-descripcion' : undefined}
                    />
                    {errores.paqueteDescripcion && (
                      <p id="error-envio-crear-paquete-descripcion" role="alert" className="mt-1 text-sm text-red-600">{errores.paqueteDescripcion}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="envio-crear-paquete-peso" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Peso (kg) *
                    </label>
                    <input
                      id="envio-crear-paquete-peso"
                      name="paquete.peso"
                      type="number"
                      step="0.1"
                      min="0.1"
                      value={paquete.peso}
                      onChange={(e) => setPaquete({ ...paquete, peso: parseFloat(e.target.value) || 0 })}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${
                        errores.paquetePeso ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                      }`}
                      placeholder="0.5"
                      aria-invalid={!!errores.paquetePeso}
                      aria-describedby={errores.paquetePeso ? 'error-envio-crear-paquete-peso' : undefined}
                    />
                    {errores.paquetePeso && (
                      <p id="error-envio-crear-paquete-peso" role="alert" className="mt-1 text-sm text-red-600">{errores.paquetePeso}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="envio-crear-paquete-categoria" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Categoría *
                    </label>
                    <select
                      id="envio-crear-paquete-categoria"
                      name="paquete.categoria"
                      value={paquete.categoria}
                      onChange={(e) => setPaquete({ ...paquete, categoria: e.target.value })}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${
                        errores.paqueteCategoria ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                      }`}
                      aria-invalid={!!errores.paqueteCategoria}
                      aria-describedby={errores.paqueteCategoria ? 'error-envio-crear-paquete-categoria' : undefined}
                    >
                      <option value="">Seleccione una categoría</option>
                      <option value="documentos">Documentos</option>
                      <option value="ropa">Ropa y textiles</option>
                      <option value="electronicos">Electrónicos</option>
                      <option value="alimentos">Alimentos</option>
                      <option value="medicamentos">Medicamentos</option>
                      <option value="otros">Otros</option>
                    </select>
                    {errores.paqueteCategoria && (
                      <p id="error-envio-crear-paquete-categoria" role="alert" className="mt-1 text-sm text-red-600">{errores.paqueteCategoria}</p>
                    )}
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Dimensiones (cm) *
                    </label>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label htmlFor="envio-crear-paquete-largo" className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                          Largo
                        </label>
                        <input
                          id="envio-crear-paquete-largo"
                          name="paquete.largo"
                          type="number"
                          min="1"
                          value={paquete.largo}
                          onChange={(e) => setPaquete({ ...paquete, largo: parseFloat(e.target.value) || 0 })}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${
                            errores.paqueteLargo ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                          }`}
                          placeholder="30"
                          aria-invalid={!!errores.paqueteLargo}
                          aria-describedby={errores.paqueteLargo ? 'error-envio-crear-paquete-largo' : undefined}
                        />
                        {errores.paqueteLargo && (
                          <p id="error-envio-crear-paquete-largo" role="alert" className="mt-1 text-sm text-red-600">{errores.paqueteLargo}</p>
                        )}
                      </div>
                      <div>
                        <label htmlFor="envio-crear-paquete-ancho" className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                          Ancho
                        </label>
                        <input
                          id="envio-crear-paquete-ancho"
                          name="paquete.ancho"
                          type="number"
                          min="1"
                          value={paquete.ancho}
                          onChange={(e) => setPaquete({ ...paquete, ancho: parseFloat(e.target.value) || 0 })}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${
                            errores.paqueteAncho ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                          }`}
                          placeholder="20"
                          aria-invalid={!!errores.paqueteAncho}
                          aria-describedby={errores.paqueteAncho ? 'error-envio-crear-paquete-ancho' : undefined}
                        />
                        {errores.paqueteAncho && (
                          <p id="error-envio-crear-paquete-ancho" role="alert" className="mt-1 text-sm text-red-600">{errores.paqueteAncho}</p>
                        )}
                      </div>
                      <div>
                        <label htmlFor="envio-crear-paquete-alto" className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                          Alto
                        </label>
                        <input
                          id="envio-crear-paquete-alto"
                          name="paquete.alto"
                          type="number"
                          min="1"
                          value={paquete.alto}
                          onChange={(e) => setPaquete({ ...paquete, alto: parseFloat(e.target.value) || 0 })}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${
                            errores.paqueteAlto ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                          }`}
                          placeholder="15"
                          aria-invalid={!!errores.paqueteAlto}
                          aria-describedby={errores.paqueteAlto ? 'error-envio-crear-paquete-alto' : undefined}
                        />
                        {errores.paqueteAlto && (
                          <p id="error-envio-crear-paquete-alto" role="alert" className="mt-1 text-sm text-red-600">{errores.paqueteAlto}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="envio-crear-paquete-valor" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Valor declarado (COP)
                    </label>
                    <input
                      id="envio-crear-paquete-valor"
                      name="paquete.valor"
                      type="number"
                      min="0"
                      value={paquete.valor}
                      onChange={(e) => setPaquete({ ...paquete, valor: parseFloat(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      placeholder="50000"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label htmlFor="envio-crear-paquete-contenido" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Contenido detallado
                    </label>
                    <textarea
                      id="envio-crear-paquete-contenido"
                      name="paquete.contenido"
                      value={paquete.contenido}
                      onChange={(e) => setPaquete({ ...paquete, contenido: e.target.value })}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      placeholder="Detalle específico del contenido (ej: 2 libros, 1 cargador portátil)..."
                    />
                  </div>

                  <div className="md:col-span-2">
                    <fieldset>
                      <legend className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Características especiales
                      </legend>
                      <div className="space-y-2">
                        <label className="flex items-center" htmlFor="envio-crear-paquete-fragil">
                          <input
                            id="envio-crear-paquete-fragil"
                            name="paquete.fragil"
                            type="checkbox"
                            checked={paquete.fragil}
                            onChange={(e) => setPaquete({ ...paquete, fragil: e.target.checked })}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Frágil</span>
                        </label>
                        <label className="flex items-center" htmlFor="envio-crear-paquete-requiere-refrigeracion">
                          <input
                            id="envio-crear-paquete-requiere-refrigeracion"
                            name="paquete.requiereRefrigeracion"
                            type="checkbox"
                            checked={paquete.requiereRefrigeracion}
                            onChange={(e) => setPaquete({ ...paquete, requiereRefrigeracion: e.target.checked })}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Requiere refrigeración</span>
                        </label>
                      </div>
                    </fieldset>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Paso 4: Configuración del Envío */}
            {pasoActual === 4 && (
              <motion.div
                key="paso4"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                className="space-y-6"
              >
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                  <TruckIcon className="h-6 w-6 text-blue-500 mr-2" />
                  Configuración del Envío
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="envio-crear-envio-tipo-servicio" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Tipo de servicio *
                    </label>
                    <select
                      id="envio-crear-envio-tipo-servicio"
                      name="envio.tipoServicio"
                      value={envio.tipoServicio}
                      onChange={(e) => setEnvio({ ...envio, tipoServicio: e.target.value })}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${
                        errores.envioTipoServicio ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                      }`}
                      aria-invalid={!!errores.envioTipoServicio}
                      aria-describedby={errores.envioTipoServicio ? 'error-envio-crear-envio-tipo-servicio' : undefined}
                    >
                      <option value="">Seleccione un servicio</option>
                      <option value="domicilio">Domicilio estándar</option>
                      <option value="express">Express (mismo día)</option>
                      <option value="programado">Entrega programada</option>
                    </select>
                    {errores.envioTipoServicio && (
                      <p id="error-envio-crear-envio-tipo-servicio" role="alert" className="mt-1 text-sm text-red-600">{errores.envioTipoServicio}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="envio-crear-envio-prioridad" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Prioridad *
                    </label>
                    <select
                      id="envio-crear-envio-prioridad"
                      name="envio.prioridad"
                      value={envio.prioridad}
                      onChange={(e) => setEnvio({ ...envio, prioridad: e.target.value })}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${
                        errores.envioPrioridad ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                      }`}
                      aria-invalid={!!errores.envioPrioridad}
                      aria-describedby={errores.envioPrioridad ? 'error-envio-crear-envio-prioridad' : undefined}
                    >
                      <option value="">Seleccione prioridad</option>
                      <option value="normal">Normal</option>
                      <option value="express">Express (+50%)</option>
                      <option value="urgente">Urgente (+100%)</option>
                    </select>
                    {errores.envioPrioridad && (
                      <p id="error-envio-crear-envio-prioridad" role="alert" className="mt-1 text-sm text-red-600">{errores.envioPrioridad}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="envio-crear-envio-fecha-entrega-deseada" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Fecha de entrega deseada
                    </label>
                    <input
                      id="envio-crear-envio-fecha-entrega-deseada"
                      name="envio.fechaEntregaDeseada"
                      type="date"
                      value={envio.fechaEntregaDeseada}
                      onChange={(e) => setEnvio({ ...envio, fechaEntregaDeseada: e.target.value })}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    />
                  </div>

                  <div>
                    <label htmlFor="envio-crear-envio-hora-entrega-deseada" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Hora preferida
                    </label>
                    <select
                      id="envio-crear-envio-hora-entrega-deseada"
                      name="envio.horaEntregaDeseada"
                      value={envio.horaEntregaDeseada}
                      onChange={(e) => setEnvio({ ...envio, horaEntregaDeseada: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    >
                      <option value="">Sin preferencia</option>
                      <option value="manana">Mañana (8:00 - 12:00)</option>
                      <option value="tarde">Tarde (12:00 - 17:00)</option>
                      <option value="noche">Noche (17:00 - 20:00)</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="envio-crear-envio-metodo-pago" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Método de pago *
                    </label>
                    <select
                      id="envio-crear-envio-metodo-pago"
                      name="envio.metodoPago"
                      value={envio.metodoPago}
                      onChange={(e) => setEnvio({ ...envio, metodoPago: e.target.value })}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${
                        errores.envioMetodoPago ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                      }`}
                      aria-invalid={!!errores.envioMetodoPago}
                      aria-describedby={errores.envioMetodoPago ? 'error-envio-crear-envio-metodo-pago' : undefined}
                    >
                      <option value="">Seleccione método de pago</option>
                      <option value="efectivo">Efectivo</option>
                      <option value="tarjeta">Tarjeta de crédito/débito</option>
                      <option value="transferencia">Transferencia bancaria</option>
                      <option value="contraentrega">Contra entrega</option>
                    </select>
                    {errores.envioMetodoPago && (
                      <p id="error-envio-crear-envio-metodo-pago" role="alert" className="mt-1 text-sm text-red-600">{errores.envioMetodoPago}</p>
                    )}
                  </div>

                  <div className="md:col-span-2">
                    <label htmlFor="envio-crear-envio-observaciones" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Observaciones adicionales
                    </label>
                    <textarea
                      id="envio-crear-envio-observaciones"
                      name="envio.observaciones"
                      value={envio.observaciones}
                      onChange={(e) => setEnvio({ ...envio, observaciones: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      placeholder="Información adicional sobre el envío..."
                    />
                  </div>

                  <div className="md:col-span-2">
                    <fieldset>
                      <legend className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                        Opciones adicionales
                      </legend>
                      <div className="space-y-3">
                        <label className="flex items-center" htmlFor="envio-crear-envio-seguro">
                          <input
                            id="envio-crear-envio-seguro"
                            name="envio.seguro"
                            type="checkbox"
                            checked={envio.seguro}
                            onChange={(e) => setEnvio({ ...envio, seguro: e.target.checked })}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                            Seguro de mercancía (+5% del valor declarado)
                          </span>
                        </label>
                        <label className="flex items-center" htmlFor="envio-crear-envio-confirmacion-entrega">
                          <input
                            id="envio-crear-envio-confirmacion-entrega"
                            name="envio.confirmacionEntrega"
                            type="checkbox"
                            checked={envio.confirmacionEntrega}
                            onChange={(e) => setEnvio({ ...envio, confirmacionEntrega: e.target.checked })}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                            Confirmación de entrega con foto
                          </span>
                        </label>
                        <label className="flex items-center" htmlFor="envio-crear-envio-notificacion-sms">
                          <input
                            id="envio-crear-envio-notificacion-sms"
                            name="envio.notificacionSMS"
                            type="checkbox"
                            checked={envio.notificacionSMS}
                            onChange={(e) => setEnvio({ ...envio, notificacionSMS: e.target.checked })}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                            Notificaciones por SMS
                          </span>
                        </label>
                        <label className="flex items-center" htmlFor="envio-crear-envio-notificacion-email">
                          <input
                            id="envio-crear-envio-notificacion-email"
                            name="envio.notificacionEmail"
                            type="checkbox"
                            checked={envio.notificacionEmail}
                            onChange={(e) => setEnvio({ ...envio, notificacionEmail: e.target.checked })}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                            Notificaciones por email
                          </span>
                        </label>
                      </div>
                    </fieldset>
                  </div>

                  {/* Costo estimado */}
                  {paquete.peso > 0 && paquete.largo > 0 && envio.prioridad && (
                    <div className="md:col-span-2 bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                      <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-2">
                        Costo Estimado
                      </h3>
                      <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                        {formatearMoneda(calcularCostoEstimado())}
                      </div>
                      <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                        *El costo final puede variar según la distancia y condiciones especiales
                      </p>
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {/* Paso 5: Confirmación */}
            {pasoActual === 5 && (
              <motion.div
                key="paso5"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                className="space-y-6"
              >
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                  <CheckCircleIcon className="h-6 w-6 text-blue-500 mr-2" />
                  Confirmación del Envío
                </h2>
                
                <div className="space-y-6">
                  {/* Resumen del remitente */}
                  <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2 flex items-center">
                      <UserIcon className="h-5 w-5 mr-2" />
                      Remitente
                    </h3>
                    <div className="text-sm text-gray-600 dark:text-gray-300">
                      <p><strong>Nombre:</strong> {remitente.nombre}</p>
                      {remitente.empresa && <p><strong>Empresa:</strong> {remitente.empresa}</p>}
                      <p><strong>Teléfono:</strong> {remitente.telefono}</p>
                      <p><strong>Dirección:</strong> {remitente.direccion}, {remitente.ciudad}</p>
                    </div>
                  </div>

                  {/* Resumen del destinatario */}
                  <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2 flex items-center">
                      <MapPinIcon className="h-5 w-5 mr-2" />
                      Destinatario
                    </h3>
                    <div className="text-sm text-gray-600 dark:text-gray-300">
                      <p><strong>Nombre:</strong> {destinatario.nombre}</p>
                      {destinatario.empresa && <p><strong>Empresa:</strong> {destinatario.empresa}</p>}
                      <p><strong>Teléfono:</strong> {destinatario.telefono}</p>
                      <p><strong>Dirección:</strong> {destinatario.direccion}, {destinatario.ciudad}</p>
                      {destinatario.instruccionesEntrega && (
                        <p><strong>Instrucciones:</strong> {destinatario.instruccionesEntrega}</p>
                      )}
                    </div>
                  </div>

                  {/* Resumen del paquete */}
                  <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2 flex items-center">
                      <CubeIcon className="h-5 w-5 mr-2" />
                      Paquete
                    </h3>
                    <div className="text-sm text-gray-600 dark:text-gray-300">
                      <p><strong>Descripción:</strong> {paquete.descripcion}</p>
                      <p><strong>Peso:</strong> {paquete.peso} kg</p>
                      <p><strong>Dimensiones:</strong> {paquete.largo} x {paquete.ancho} x {paquete.alto} cm</p>
                      <p><strong>Categoría:</strong> {paquete.categoria}</p>
                      {paquete.valor > 0 && <p><strong>Valor declarado:</strong> {formatearMoneda(paquete.valor)}</p>}
                      {(paquete.fragil || paquete.requiereRefrigeracion) && (
                        <p><strong>Características:</strong> 
                          {paquete.fragil && ' Frágil'}
                          {paquete.fragil && paquete.requiereRefrigeracion && ','}
                          {paquete.requiereRefrigeracion && ' Requiere refrigeración'}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Resumen del envío */}
                  <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2 flex items-center">
                      <TruckIcon className="h-5 w-5 mr-2" />
                      Configuración del Envío
                    </h3>
                    <div className="text-sm text-gray-600 dark:text-gray-300">
                      <p><strong>Tipo de servicio:</strong> {envio.tipoServicio}</p>
                      <p><strong>Prioridad:</strong> {envio.prioridad}</p>
                      {envio.fechaEntregaDeseada && (
                        <p><strong>Fecha deseada:</strong> {new Date(envio.fechaEntregaDeseada).toLocaleDateString('es-CO')}</p>
                      )}
                      {envio.horaEntregaDeseada && <p><strong>Hora preferida:</strong> {envio.horaEntregaDeseada}</p>}
                      <p><strong>Método de pago:</strong> {envio.metodoPago}</p>
                      {envio.observaciones && <p><strong>Observaciones:</strong> {envio.observaciones}</p>}
                    </div>
                  </div>

                  {/* Costo final */}
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-6 rounded-lg">
                    <div className="flex justify-between items-center">
                      <h3 className="text-xl font-semibold text-blue-900 dark:text-blue-100">
                        Costo Total
                      </h3>
                      <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                        {formatearMoneda(calcularCostoEstimado())}
                      </div>
                    </div>
                    <p className="text-sm text-blue-700 dark:text-blue-300 mt-2">
                      Incluye todos los servicios seleccionados
                    </p>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Botones de navegación */}
          <div className="flex justify-between pt-6 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={pasoAnterior}
              disabled={pasoActual === 1}
              className="flex items-center px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              Anterior
            </button>

            {pasoActual < 5 ? (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={siguientePaso}
                className="flex items-center px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              >
                Siguiente
                <ArrowRightIcon className="h-4 w-4 ml-2" />
              </motion.button>
            ) : (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={enviarFormulario}
                disabled={cargando}
                className="flex items-center px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
              >
                {cargando ? (
                  <>
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      className="w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"
                    />
                    Creando...
                  </>
                ) : (
                  <>
                    <CheckCircleIcon className="h-4 w-4 mr-2" />
                    Crear Envío
                  </>
                )}
              </motion.button>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  )
}

export default PaginaCrearEnvio
